/**
 * Visual Step Context
 * 
 * Manages the visual step progress display independently from actual navigation steps.
 * This allows sub-steps (like time selection and bay selection) to update the progress
 * indicator without triggering actual navigation.
 * 
 * Use cases:
 * - Showing "Bay Selection" step while still in TIME_SELECTION context step
 * - Desktop progressive disclosure updating step progress
 * - Mobile substep navigation updating step progress
 * 
 * @module booking/context
 */

import { 
  createContext, 
  useState, 
  useCallback, 
  useContext,
  type ReactNode 
} from 'react';

/**
 * Visual step IDs for UI display
 */
export const VISUAL_STEPS = {
  BOOKING_TIME: 0,
  BAY_SELECTION: 1,
  CONFIRMATION: 2,
} as const;

export type VisualStepId = typeof VISUAL_STEPS[keyof typeof VISUAL_STEPS];

/**
 * Visual Step Context Value
 */
export interface VisualStepContextValue {
  /** Current visual step (what user sees in progress indicator) */
  visualStep: VisualStepId;
  /** Total number of visual steps */
  totalVisualSteps: number;
  /** Set the visual step (does not affect navigation) */
  setVisualStep: (step: VisualStepId) => void;
  /** Reset visual step to initial state */
  resetVisualStep: () => void;
  /**
   * The payment step asks for no card — account credit covers it, nothing is due, or the club
   * collects at the counter — so the page header says "review and confirm", not "add your
   * payment details". Set by that step, which holds the quote; cleared when it unmounts.
   */
  paymentNeedsNoCard: boolean;
  setPaymentNeedsNoCard: (needsNoCard: boolean) => void;
}

/**
 * Visual Step Context
 */
export const VisualStepContext = createContext<VisualStepContextValue | null>(null);

/**
 * Props for VisualStepProvider
 */
interface VisualStepProviderProps {
  children: ReactNode;
  /** Initial visual step (defaults to BOOKING_TIME) */
  initialStep?: VisualStepId;
}

/**
 * Visual Step Provider
 * 
 * Manages visual step display for the booking flow.
 * This is separate from navigation logic to allow sub-steps.
 * 
 * @example
 * ```tsx
 * <VisualStepProvider>
 *   <BookingFlowPage />
 * </VisualStepProvider>
 * ```
 */
export function VisualStepProvider({ 
  children, 
  initialStep = VISUAL_STEPS.BOOKING_TIME 
}: VisualStepProviderProps) {
  const [visualStep, setVisualStepState] = useState<VisualStepId>(initialStep);
  const [paymentNeedsNoCard, setPaymentNeedsNoCard] = useState(false);
  
  const totalVisualSteps = 3;
  
  /**
   * Set the visual step
   * This updates what the user sees in the step progress indicator
   * but does NOT trigger any navigation
   */
  const setVisualStep = useCallback((step: VisualStepId) => {
    if (step < 0 || step >= totalVisualSteps) {
      console.warn(`[VisualStepContext] Invalid visual step: ${step}`);
      return;
    }
    
    if (import.meta.env.DEV) {
      console.log(`[VisualStepContext] Visual step changed: ${visualStep} → ${step}`);
    }
    
    setVisualStepState(step);
  }, [visualStep, totalVisualSteps]);
  
  /**
   * Reset visual step to initial state
   */
  const resetVisualStep = useCallback(() => {
    setVisualStepState(VISUAL_STEPS.BOOKING_TIME);
  }, []);
  
  const value: VisualStepContextValue = {
    visualStep,
    totalVisualSteps,
    setVisualStep,
    resetVisualStep,
    paymentNeedsNoCard,
    setPaymentNeedsNoCard,
  };
  
  return (
    <VisualStepContext.Provider value={value}>
      {children}
    </VisualStepContext.Provider>
  );
}

/**
 * Hook to access visual step context
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { visualStep, setVisualStep } = useVisualStep();
 *   
 *   const handleTimeSelected = () => {
 *     setVisualStep(VISUAL_STEPS.BAY_SELECTION);
 *   };
 * }
 * ```
 */
export function useVisualStep(): VisualStepContextValue {
  const context = useContext(VisualStepContext);
  
  if (!context) {
    throw new Error('useVisualStep must be used within VisualStepProvider');
  }
  
  return context;
}
